import axios from "axios";
import { store } from "../redux/store";
import { logout, updateTokens } from "../redux/auth/authSlice";
import { showToast } from "../componants/toast/toastService";
import endpoints from "./endpoints";

// Extracts the specific error detail from a server error payload, deliberately
// skipping `data.message` — that field is generic (e.g. "Invalid data") and is
// used as the toast title instead, while this becomes the toast description.
const getServerErrorDetail = (data) => {
  if (!data) return null;

  if (typeof data.detail === "string" && data.detail.trim()) {
    return data.detail;
  }
  if (typeof data.error === "string" && data.error.trim()) {
    return data.error;
  }
  if (data.error && typeof data.error === "object") {
    const nested = Object.values(data.error)
      .flat()
      .find((item) => typeof item === "string" && item.trim());
    if (nested) return nested;
  }
  // Nested field errors under data: { data: { field: ["msg"] } }
  if (data.data && typeof data.data === "object" && !Array.isArray(data.data)) {
    const nested = Object.values(data.data)
      .flat()
      .find((item) => typeof item === "string" && item.trim());
    if (nested) return nested;
  }
  if (data.errors && typeof data.errors === "object") {
    const nested = Object.values(data.errors)
      .flat()
      .find((item) => typeof item === "string" && item.trim());
    if (nested) return nested;
  }

  const fieldErrors = Object.entries(data)
    .filter(
      ([key]) =>
        ![
          "status",
          "statusCode",
          "message",
          "error",
          "data",
          "errors",
        ].includes(key),
    )
    .map(([key, value]) => {
      const msg = Array.isArray(value) ? value[0] : value;
      return typeof msg === "string" && msg.trim() ? `${key}: ${msg}` : null;
    })
    .filter(Boolean);

  return fieldErrors.length ? fieldErrors.join(", ") : null;
};

const api = axios.create({
  baseURL: process.env.REACT_APP_API_BASE_URL,
  timeout: 15000,
  headers: {
    "Content-Type": "application/json",
    Accept: "application/json",
  },
});

// ─── Refresh token queue ──────────────────────────────────────────────────────
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach((p) => (error ? p.reject(error) : p.resolve(token)));
  failedQueue = [];
};

// ─── Request interceptor ──────────────────────────────────────────────────────
api.interceptors.request.use(
  (config) => {
    const token = store.getState()?.auth?.access_token;
    const allTimeZones = Intl.supportedValuesOf("timeZone");
    const currentTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    if (currentTimeZone) {
      config.headers["X-TIMEZONE"] = currentTimeZone;
    }
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    // When sending FormData, remove Content-Type so axios sets it
    // automatically with the correct multipart boundary
    if (config.data instanceof FormData) {
      delete config.headers["Content-Type"];
    }
    return config;
  },
  (error) => Promise.reject(error),
);

// ─── Response interceptor ─────────────────────────────────────────────────────
api.interceptors.response.use(
  (response) => {
    const { silent } = response.config;
    if (!silent && response.data?.message) {
      showToast(
        { title: "Success!", message: response.data.message },
        "success",
      );
    }
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    // Network / no response from server
    if (!error.response) {
      showToast("Network error. Please check your connection.", "error");
      return Promise.reject(error);
    }

    const { status, data } = error.response;

    // ── 401: try refresh token, then retry ────────────────────────────────────
    if (status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return api(originalRequest);
          })
          .catch((err) => Promise.reject(err));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      const refreshToken = store.getState()?.auth?.refresh_token;

      if (!refreshToken) {
        isRefreshing = false;
        processQueue(error, null);
        store.dispatch(logout());
        showToast("Session expired. Please login again.", "error");
        return Promise.reject(error);
      }

      try {
        const { data: refreshData } = await axios.post(
          `${process.env.REACT_APP_API_BASE_URL}${endpoints.auth.refreshToken}`,
          { refresh: refreshToken },
        );

        // response shape: { data: { access, refresh? } }
        const newAccessToken = refreshData?.data?.access || refreshData?.access;
        const newRefreshToken =
          refreshData?.data?.refresh || refreshData?.refresh;

        store.dispatch(
          updateTokens({
            access_token: newAccessToken,
            ...(newRefreshToken && { refresh_token: newRefreshToken }),
          }),
        );

        api.defaults.headers.common["Authorization"] =
          `Bearer ${newAccessToken}`;
        originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;

        processQueue(null, newAccessToken);
        return api(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError, null);
        store.dispatch(logout());
        showToast("Session expired. Please login again.", "error");
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    // ── Other HTTP errors — always toast (silent only hides success toasts) ───
    // Title = the API's generic message (e.g. "Invalid data") when present, else
    // a status-based default. Description = the specific reason, when available.
    const apiTitle =
      typeof data?.message === "string" && data.message.trim()
        ? data.message
        : null;
    const detail = getServerErrorDetail(data);

    const toastFor = (defaultTitle, fallbackDetail, toastType = "error") =>
      showToast(
        { title: apiTitle || defaultTitle, message: detail || fallbackDetail },
        toastType,
      );

    switch (status) {
      case 400:
        toastFor(
          "Invalid Request",
          "Invalid request. Please check your input.",
        );
        break;
      case 403:
        toastFor(
          "Access Denied",
          "You don't have permission to perform this action.",
          "warning",
        );
        break;
      case 404:
        toastFor("Not Found", "Requested resource not found.");
        break;
      case 422:
        toastFor(
          "Validation Failed",
          "Validation failed. Please check your input.",
          "warning",
        );
        break;
      case 429:
        toastFor("Too Many Requests", "Please slow down.", "warning");
        break;
      case 500: {
        const isGenericAxiosText =
          detail && /^request failed with status code/i.test(detail);
        showToast(
          {
            title: apiTitle || "Server Error",
            message:
              !isGenericAxiosText && detail
                ? detail
                : "Server error. Please try again later.",
          },
          "error",
        );
        break;
      }
      case 502:
      case 503:
      case 504:
        toastFor("Service Unavailable", "Please try again later.");
        break;
      default:
        toastFor("Error", "Something went wrong. Please try again.");
    }

    // Mark as handled so callers can safely swallow without console AxiosError noise
    error.toastShown = true;
    return Promise.reject(error);
  },
);

// ─── Service methods ──────────────────────────────────────────────────────────
const service = {
  get: (url, params = {}, config = {}) => api.get(url, { params, ...config }),

  post: (url, data = {}, config = {}) => api.post(url, data, config),

  put: (url, data = {}, config = {}) => api.put(url, data, config),

  patch: (url, data = {}, config = {}) => api.patch(url, data, config),

  delete: (url, config = {}) => api.delete(url, config),

  upload: (url, data, config = {}) => api.post(url, data, config),

  patchForm: (url, data, config = {}) => api.patch(url, data, config),

  download: (url, config = {}) =>
    api.get(url, {
      ...config,
      responseType: "blob",
      headers: {
        // Drop axios default Accept/Content-Type; callers can override
        Accept: "*/*",
        "Content-Type": false,
        ...(config.headers || {}),
      },
    }),
};

export default service;
